const TOFFEE_WISHLIST_KEY = "toffee:wishlist:v1";
const TOFFEE_WISHLIST_EVENT = "toffee:wishlist:change";
const TOFFEE_WISHLIST_MAX = 50;
const TOFFEE_WISHLIST_TOAST_MS = 4000;

const toffeeWishlistState = {
  items: [],
  storageAvailable: true,
  productCache: new Map(),
  cartVariantIds: new Set(),
  renderToken: 0,
  lastUndo: null,
  toastTimer: null
};

function toffeeWishlistNormalizeItem(rawItem) {
  if (!rawItem || typeof rawItem !== "object") {
    return null;
  }

  const productId = Number(rawItem.productId);
  const variantId = rawItem.variantId == null || rawItem.variantId === "" ? null : Number(rawItem.variantId);
  const handle = typeof rawItem.handle === "string" ? rawItem.handle.trim() : "";
  const addedAt = typeof rawItem.addedAt === "string" && !Number.isNaN(Date.parse(rawItem.addedAt))
    ? rawItem.addedAt
    : new Date().toISOString();

  if (!Number.isSafeInteger(productId) || productId <= 0 || !/^[a-z0-9][a-z0-9-]*$/.test(handle)) {
    return null;
  }

  return {
    productId: productId,
    handle: handle,
    variantId: Number.isSafeInteger(variantId) && variantId > 0 ? variantId : null,
    addedAt: addedAt
  };
}

function toffeeWishlistRead() {
  try {
    const rawValue = window.localStorage.getItem(TOFFEE_WISHLIST_KEY);

    if (!rawValue) {
      return [];
    }

    const parsedValue = JSON.parse(rawValue);

    if (!parsedValue || parsedValue.version !== 1 || !Array.isArray(parsedValue.items)) {
      return [];
    }

    const uniqueItems = new Map();

    for (const rawItem of parsedValue.items) {
      const item = toffeeWishlistNormalizeItem(rawItem);

      if (item && !uniqueItems.has(item.productId)) {
        uniqueItems.set(item.productId, item);
      }

      if (uniqueItems.size >= TOFFEE_WISHLIST_MAX) {
        break;
      }
    }

    return Array.from(uniqueItems.values()).sort(function(a, b) {
      return Date.parse(b.addedAt) - Date.parse(a.addedAt);
    });
  } catch (error) {
    toffeeWishlistState.storageAvailable = false;
    return [];
  }
}

function toffeeWishlistCommit(nextItems, detail) {
  const normalizedItems = nextItems.slice(0, TOFFEE_WISHLIST_MAX);
  let persisted = true;

  try {
    window.localStorage.setItem(TOFFEE_WISHLIST_KEY, JSON.stringify({
      version: 1,
      items: normalizedItems
    }));
    toffeeWishlistState.storageAvailable = true;
  } catch (error) {
    persisted = false;
    toffeeWishlistState.storageAvailable = false;
  }

  toffeeWishlistState.items = normalizedItems;
  toffeeWishlistSyncUi();

  document.dispatchEvent(new CustomEvent(TOFFEE_WISHLIST_EVENT, {
    detail: Object.assign({
      items: normalizedItems.slice(),
      count: normalizedItems.length,
      persisted: persisted
    }, detail || {})
  }));

  if (!persisted) {
    toffeeWishlistAnnounce("Nur für diese Sitzung gespeichert. Dein Browser blockiert die dauerhafte Speicherung.", true);
    toffeeWishlistTrack("wishlist_error", {
      source: detail && detail.source ? detail.source : "unknown",
      productId: detail && detail.productId ? detail.productId : null,
      variantId: detail && detail.variantId ? detail.variantId : null,
      error: "storage_unavailable"
    });
  }

  return persisted;
}

function toffeeWishlistFind(productId) {
  return toffeeWishlistState.items.find(function(item) {
    return item.productId === Number(productId);
  }) || null;
}

function toffeeWishlistAdd(item, source) {
  if (toffeeWishlistState.items.length >= TOFFEE_WISHLIST_MAX) {
    const maxError = new Error("wishlist_max");
    maxError.code = "WISHLIST_MAX";
    throw maxError;
  }

  const nextItems = [item].concat(toffeeWishlistState.items.filter(function(entry) {
    return entry.productId !== item.productId;
  }));

  toffeeWishlistCommit(nextItems, {
    action: "add",
    source: source,
    productId: item.productId,
    variantId: item.variantId
  });

  return item;
}

function toffeeWishlistRemove(productId, source) {
  const index = toffeeWishlistState.items.findIndex(function(item) {
    return item.productId === Number(productId);
  });

  if (index < 0) {
    return null;
  }

  const removedItem = toffeeWishlistState.items[index];
  const nextItems = toffeeWishlistState.items.slice();
  nextItems.splice(index, 1);

  toffeeWishlistCommit(nextItems, {
    action: "remove",
    source: source,
    productId: removedItem.productId,
    variantId: removedItem.variantId
  });

  return {
    item: removedItem,
    index: index
  };
}

function toffeeWishlistRestore(item, index, source) {
  const nextItems = toffeeWishlistState.items.filter(function(entry) {
    return entry.productId !== item.productId;
  });
  const safeIndex = Math.max(0, Math.min(index, nextItems.length));
  nextItems.splice(safeIndex, 0, item);

  toffeeWishlistCommit(nextItems, {
    action: "restore",
    source: source,
    productId: item.productId,
    variantId: item.variantId
  });
}

function toffeeWishlistUpdateVariant(productId, variantId) {
  const numericProductId = Number(productId);
  const numericVariantId = Number(variantId);
  let changed = false;

  const nextItems = toffeeWishlistState.items.map(function(item) {
    if (item.productId !== numericProductId || item.variantId === numericVariantId) {
      return item;
    }

    changed = true;
    return Object.assign({}, item, {
      variantId: Number.isSafeInteger(numericVariantId) && numericVariantId > 0 ? numericVariantId : null
    });
  });

  if (changed) {
    toffeeWishlistCommit(nextItems, {
      action: "variant_update",
      source: "pdp",
      productId: numericProductId,
      variantId: numericVariantId
    });
  }
}

function toffeeWishlistGetButtonVariant(button) {
  const formId = button.dataset.productFormId;

  if (formId) {
    const form = document.getElementById(formId);
    const idInput = form ? form.querySelector('[name="id"]') : null;
    const currentId = idInput ? Number(idInput.value) : null;

    if (Number.isSafeInteger(currentId) && currentId > 0) {
      return currentId;
    }
  }

  const datasetId = Number(button.dataset.variantId);
  return Number.isSafeInteger(datasetId) && datasetId > 0 ? datasetId : null;
}

function toffeeWishlistToggle(button) {
  const productId = Number(button.dataset.productId);
  const productTitle = button.dataset.productTitle || "Produkt";
  const source = button.dataset.source || "unknown";
  const savedItem = toffeeWishlistFind(productId);

  if (savedItem) {
    const removal = toffeeWishlistRemove(productId, source);

    if (removal) {
      toffeeWishlistState.lastUndo = {
        type: "restore",
        item: removal.item,
        index: removal.index,
        source: source
      };
      toffeeWishlistShowToast("Entfernt", false);
      toffeeWishlistTrack("wishlist_remove", {
        source: source,
        productId: productId,
        variantId: savedItem.variantId
      });
    }

    return;
  }

  try {
    const item = {
      productId: productId,
      handle: button.dataset.productHandle,
      variantId: toffeeWishlistGetButtonVariant(button),
      addedAt: new Date().toISOString()
    };

    toffeeWishlistAdd(item, source);
    toffeeWishlistState.lastUndo = {
      type: "remove",
      item: item,
      index: 0,
      source: source
    };
    toffeeWishlistShowToast("Als Lieblingsstück gespeichert", true);

    document.querySelectorAll('[data-toffee-wishlist-button][data-product-id="' + productId + '"]').forEach(function(currentButton) {
      currentButton.classList.remove("is-just-saved");
      void currentButton.offsetWidth;
      currentButton.classList.add("is-just-saved");
      window.setTimeout(function() {
        currentButton.classList.remove("is-just-saved");
      }, 550);
    });

    toffeeWishlistTrack("wishlist_add", {
      source: source,
      productId: productId,
      variantId: item.variantId
    });
  } catch (error) {
    if (error && error.code === "WISHLIST_MAX") {
      toffeeWishlistShowToast("Du kannst maximal 50 Lieblingsstücke speichern.", false, true);
      toffeeWishlistAnnounce("Du kannst maximal 50 Lieblingsstücke speichern.", true);
    } else {
      toffeeWishlistShowToast("Konnte nicht gespeichert werden. Bitte versuche es erneut.", false, true);
      toffeeWishlistAnnounce("Konnte nicht gespeichert werden. Bitte versuche es erneut.", true);
    }

    toffeeWishlistTrack("wishlist_error", {
      source: source,
      productId: productId,
      variantId: null,
      error: error && error.code ? error.code : "toggle_error"
    });
  }
}

function toffeeWishlistSyncUi() {
  const count = toffeeWishlistState.items.length;

  document.querySelectorAll("[data-toffee-wishlist-button]").forEach(function(button) {
    const productId = Number(button.dataset.productId);
    const isSaved = Boolean(toffeeWishlistFind(productId));
    const title = button.dataset.productTitle || "Produkt";

    button.setAttribute("aria-pressed", isSaved ? "true" : "false");
    button.setAttribute("aria-label", title + (isSaved
      ? " aus Lieblingsstücken entfernen"
      : " zu Lieblingsstücken hinzufügen"));
  });

  document.querySelectorAll("[data-toffee-wishlist-count]").forEach(function(countElement) {
    countElement.textContent = count > 99 ? "99+" : String(count);
    countElement.dataset.count = String(count);
  });

  document.querySelectorAll("[data-toffee-wishlist-open]").forEach(function(button) {
    button.dataset.hasItems = count > 0 ? "true" : "false";
    button.setAttribute("aria-label", count === 0
      ? "Lieblingsstücke öffnen, noch keine gespeichert"
      : "Lieblingsstücke öffnen, " + count + " gespeichert");
  });
}

function toffeeWishlistAnnounce(message, isError) {
  const status = document.querySelector(isError
    ? "[data-toffee-wishlist-status]"
    : "[data-toffee-wishlist-drawer-status]") || document.querySelector("[data-toffee-wishlist-status]");

  if (status) {
    status.textContent = "";
    window.requestAnimationFrame(function() {
      status.textContent = message;
    });
  }
}

function toffeeWishlistTrack(eventName, payload) {
  const detail = Object.assign({
    source: "unknown",
    productId: null,
    variantId: null,
    itemCount: toffeeWishlistState.items.length,
    storageMode: "local"
  }, payload || {});

  document.dispatchEvent(new CustomEvent(eventName, {
    detail: detail
  }));

  if (Array.isArray(window.dataLayer)) {
    window.dataLayer.push(Object.assign({
      event: eventName
    }, detail));
  }
}

function toffeeWishlistHideToast() {
  const toast = document.querySelector("[data-toffee-wishlist-toast]");

  if (!toast) {
    return;
  }

  window.clearTimeout(toffeeWishlistState.toastTimer);
  toffeeWishlistState.toastTimer = null;
  toast.hidden = true;
}

function toffeeWishlistFocusDrawerClose(drawer) {
  window.requestAnimationFrame(function() {
    if (!drawer.hasAttribute("open")) {
      return;
    }

    const shadowActive = drawer.shadowRoot ? drawer.shadowRoot.activeElement : null;
    const lightActive = document.activeElement;
    const lightActiveInside = lightActive && lightActive !== drawer && drawer.contains(lightActive);

    if (shadowActive || lightActiveInside) {
      return;
    }

    const closeButtons = drawer.shadowRoot
      ? Array.from(drawer.shadowRoot.querySelectorAll('[part="close-button"], [part="outside-close-button"]'))
      : [];
    const closeButton = closeButtons.find(function(button) {
      return button.getClientRects().length > 0;
    }) || closeButtons[0] || null;

    if (closeButton instanceof HTMLElement) {
      closeButton.focus({ preventScroll: true });
    }
  });
}

function toffeeWishlistHandleDrawerOpen(event) {
  const drawer = event.target;

  if (!(drawer instanceof HTMLElement) || drawer.id !== "toffee-wishlist-drawer" || !drawer.hasAttribute("open")) {
    return;
  }

  toffeeWishlistFocusDrawerClose(drawer);
}

function toffeeWishlistArmToast() {
  window.clearTimeout(toffeeWishlistState.toastTimer);
  toffeeWishlistState.toastTimer = window.setTimeout(toffeeWishlistHideToast, TOFFEE_WISHLIST_TOAST_MS);
}

function toffeeWishlistShowToast(message, showView, isError) {
  const toast = document.querySelector("[data-toffee-wishlist-toast]");
  const messageElement = toast ? toast.querySelector("[data-toast-message]") : null;
  const viewButton = toast ? toast.querySelector("[data-toast-view]") : null;
  const undoButton = toast ? toast.querySelector("[data-toast-undo]") : null;

  if (!toast || !messageElement || !viewButton || !undoButton) {
    return;
  }

  messageElement.textContent = message;
  viewButton.hidden = !showView;
  undoButton.hidden = isError || !toffeeWishlistState.lastUndo;
  toast.hidden = false;
  toffeeWishlistArmToast();
  toffeeWishlistAnnounce(message, Boolean(isError));
}

async function toffeeWishlistMapLimit(items, limit, mapper) {
  const results = new Array(items.length);
  let nextIndex = 0;

  async function worker() {
    while (nextIndex < items.length) {
      const currentIndex = nextIndex;
      nextIndex += 1;
      results[currentIndex] = await mapper(items[currentIndex], currentIndex);
    }
  }

  const workers = [];
  const workerCount = Math.min(limit, items.length);

  for (let i = 0; i < workerCount; i += 1) {
    workers.push(worker());
  }

  await Promise.all(workers);
  return results;
}

function toffeeWishlistRootUrl() {
  const drawer = document.getElementById("toffee-wishlist-drawer");
  const root = drawer && drawer.dataset.rootUrl
    ? drawer.dataset.rootUrl
    : window.Shopify && window.Shopify.routes && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : "/";

  return root.endsWith("/") ? root : root + "/";
}

function toffeeWishlistFetchProduct(item, force) {
  if (force) {
    toffeeWishlistState.productCache.delete(item.handle);
  }

  if (toffeeWishlistState.productCache.has(item.handle)) {
    return toffeeWishlistState.productCache.get(item.handle);
  }

  const request = fetch(toffeeWishlistRootUrl() + "products/" + encodeURIComponent(item.handle) + ".js", {
    headers: {
      Accept: "application/json"
    }
  }).then(function(response) {
    if (!response.ok) {
      const error = new Error("product_request_failed");
      error.status = response.status;
      throw error;
    }

    return response.json();
  }).then(function(product) {
    return {
      state: "ok",
      item: item,
      product: product
    };
  }).catch(function(error) {
    if (!error || error.status !== 404) {
      toffeeWishlistState.productCache.delete(item.handle);
    }

    return {
      state: error && error.status === 404 ? "missing" : "error",
      item: item,
      error: error
    };
  });

  toffeeWishlistState.productCache.set(item.handle, request);
  return request;
}

function toffeeWishlistFormatMoney(cents) {
  const drawer = document.getElementById("toffee-wishlist-drawer");
  const currency = drawer && drawer.dataset.currency ? drawer.dataset.currency : "CHF";
  const locale = drawer && drawer.dataset.locale ? drawer.dataset.locale : "de-CH";

  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currency
    }).format(Number(cents || 0) / 100);
  } catch (error) {
    return currency + " " + (Number(cents || 0) / 100).toFixed(2);
  }
}

function toffeeWishlistShippingTotal(cart) {
  if (!cart || !Array.isArray(cart.items)) {
    return 0;
  }

  const shippingSubtotal = cart.items.reduce(function(total, item) {
    return item && item.requires_shipping
      ? total + Number(item.final_line_price || 0)
      : total;
  }, 0);

  const cartDiscount = Array.isArray(cart.cart_level_discount_applications)
    ? cart.cart_level_discount_applications.reduce(function(total, discount) {
        return total + Number(discount && discount.total_allocated_amount || 0);
      }, 0)
    : 0;

  return Math.max(0, shippingSubtotal - cartDiscount);
}

function toffeeWishlistUpdateShippingProgress(cart) {
  const drawer = document.getElementById("toffee-wishlist-drawer");
  const message = drawer ? drawer.querySelector("[data-toffee-shipping-message]") : null;
  const progress = drawer ? drawer.querySelector("[data-toffee-shipping-progress]") : null;
  const fill = drawer ? drawer.querySelector("[data-toffee-shipping-progress-fill]") : null;
  const threshold = drawer ? Number(drawer.dataset.freeShippingThreshold) : 0;

  if (!drawer || !message || !progress || !fill || !Number.isFinite(threshold) || threshold <= 0) {
    return;
  }

  const shippingTotal = toffeeWishlistShippingTotal(cart);
  const remaining = Math.max(0, threshold - shippingTotal);
  const percentage = Math.max(0, Math.min(100, Math.round(shippingTotal / threshold * 100)));

  message.textContent = remaining > 0
    ? "Noch " + toffeeWishlistFormatMoney(remaining) + " bis zum kostenlosen Versand."
    : "Kostenloser Versand ist erreicht.";
  progress.setAttribute("aria-valuenow", String(percentage));
  progress.setAttribute("aria-valuetext", remaining > 0
    ? toffeeWishlistFormatMoney(remaining) + " fehlen noch"
    : "Kostenloser Versand erreicht");
  fill.style.width = percentage + "%";
  progress.dataset.reached = remaining === 0 ? "true" : "false";
}

async function toffeeWishlistFetchCart() {
  try {
    const response = await fetch(toffeeWishlistRootUrl() + "cart.js", {
      headers: {
        Accept: "application/json"
      }
    });

    if (!response.ok) {
      throw new Error("cart_request_failed");
    }

    const cart = await response.json();
    toffeeWishlistState.cartVariantIds = new Set((cart.items || []).map(function(item) {
      return Number(item.variant_id || item.id);
    }));
    return cart;
  } catch (error) {
    toffeeWishlistState.cartVariantIds = new Set();
    return null;
  }
}

function toffeeWishlistSetProductLinks(node, url) {
  node.querySelectorAll("[data-product-link]").forEach(function(link) {
    link.href = url;
  });
}

function toffeeWishlistBuildItem(result) {
  const template = document.querySelector("[data-toffee-wishlist-item-template]");

  if (!template) {
    return null;
  }

  const node = template.content.firstElementChild.cloneNode(true);
  const item = result.item;
  node.dataset.productId = String(item.productId);
  node.dataset.handle = item.handle;

  const removeButton = node.querySelector("[data-remove-item]");
  removeButton.dataset.productId = String(item.productId);

  if (result.state !== "ok") {
    const missing = result.state === "missing";
    const fallbackUrl = toffeeWishlistRootUrl() + "products/" + item.handle;
    toffeeWishlistSetProductLinks(node, fallbackUrl);
    node.querySelector("[data-product-title]").textContent = missing ? "Nicht mehr verfügbar" : "Produktdaten nicht verfügbar";
    node.querySelector("[data-product-price]").textContent = "";

    const availability = node.querySelector("[data-product-availability]");
    availability.dataset.state = "error";
    availability.textContent = missing ? "Dieses Produkt ist nicht mehr erreichbar." : "Daten konnten nicht geladen werden.";

    const image = node.querySelector("[data-product-image]");
    const fallback = node.querySelector("[data-image-fallback]");
    image.hidden = true;
    fallback.hidden = false;
    node.querySelector("[data-add-to-cart]").hidden = true;
    node.querySelector("[data-choose-options]").hidden = true;

    const retryButton = node.querySelector("[data-retry-product]");
    retryButton.hidden = missing;
    retryButton.dataset.productId = String(item.productId);
    return node;
  }

  const product = result.product;
  const variants = Array.isArray(product.variants) ? product.variants : [];
  let variant = null;

  if (item.variantId) {
    variant = variants.find(function(entry) {
      return Number(entry.id) === Number(item.variantId);
    }) || null;
  }

  if (!variant && variants.length === 1) {
    variant = variants[0];
  }

  const productUrl = product.url || toffeeWishlistRootUrl() + "products/" + item.handle;
  toffeeWishlistSetProductLinks(node, productUrl);
  node.querySelector("[data-product-title]").textContent = product.title || item.handle;

  const image = node.querySelector("[data-product-image]");
  const fallback = node.querySelector("[data-image-fallback]");
  const imageUrl = variant && variant.featured_image && variant.featured_image.src
    ? variant.featured_image.src
    : product.featured_image || (Array.isArray(product.images) ? product.images[0] : null);

  if (imageUrl) {
    image.src = imageUrl;
    image.alt = product.title || "";
    image.hidden = false;
    fallback.hidden = true;
  } else {
    image.hidden = true;
    fallback.hidden = false;
  }

  const variantElement = node.querySelector("[data-product-variant]");

  if (variant && variant.title && variant.title !== "Default Title") {
    variantElement.textContent = variant.title;
    variantElement.hidden = false;
  }

  const currentPrice = variant ? variant.price : product.price;
  const comparePrice = variant ? variant.compare_at_price : product.compare_at_price;
  node.querySelector("[data-product-price]").textContent = toffeeWishlistFormatMoney(currentPrice);

  const compareElement = node.querySelector("[data-product-compare-price]");
  if (comparePrice && Number(comparePrice) > Number(currentPrice)) {
    compareElement.textContent = toffeeWishlistFormatMoney(comparePrice);
    compareElement.hidden = false;
  }

  const availability = node.querySelector("[data-product-availability]");
  const addButton = node.querySelector("[data-add-to-cart]");
  const chooseLink = node.querySelector("[data-choose-options]");
  const cartState = node.querySelector("[data-cart-state]");

  if (!variant && variants.length > 1) {
    availability.dataset.state = "unavailable";
    availability.textContent = "Bitte zuerst eine Option wählen.";
    addButton.hidden = true;
    chooseLink.hidden = false;
    chooseLink.href = productUrl;
    chooseLink.textContent = product.type === "Gutschein" || /gutschein/i.test(product.title || "")
      ? "Nennwert wählen"
      : "Optionen wählen";
  } else if (!variant || !variant.available) {
    availability.dataset.state = "unavailable";
    availability.textContent = "Zurzeit nicht verfügbar";
    addButton.hidden = true;
    chooseLink.hidden = false;
    chooseLink.href = productUrl;
    chooseLink.textContent = "Produkt ansehen";
  } else {
    availability.dataset.state = "available";
    availability.textContent = "Verfügbar";
    addButton.hidden = false;
    addButton.dataset.variantId = String(variant.id);
    addButton.dataset.productId = String(item.productId);
    chooseLink.hidden = true;

    if (toffeeWishlistState.cartVariantIds.has(Number(variant.id))) {
      cartState.hidden = false;
    }
  }

  return node;
}

async function toffeeWishlistRenderDrawer(forceProductId) {
  const drawer = document.getElementById("toffee-wishlist-drawer");
  const body = drawer ? drawer.querySelector("[data-toffee-wishlist-body]") : null;
  const empty = drawer ? drawer.querySelector("[data-toffee-wishlist-empty]") : null;
  const loading = drawer ? drawer.querySelector("[data-toffee-wishlist-loading]") : null;
  const itemsContainer = drawer ? drawer.querySelector("[data-toffee-wishlist-items]") : null;

  if (!drawer || !body || !empty || !loading || !itemsContainer) {
    return;
  }

  const renderToken = ++toffeeWishlistState.renderToken;
  const items = toffeeWishlistState.items.slice();
  const cart = await toffeeWishlistFetchCart();

  if (renderToken !== toffeeWishlistState.renderToken) {
    return;
  }

  toffeeWishlistUpdateShippingProgress(cart);

  if (items.length === 0) {
    body.setAttribute("aria-busy", "false");
    empty.hidden = false;
    loading.hidden = true;
    itemsContainer.hidden = true;
    itemsContainer.replaceChildren();
    return;
  }

  empty.hidden = true;
  loading.hidden = false;
  itemsContainer.hidden = true;
  body.setAttribute("aria-busy", "true");

  const results = await toffeeWishlistMapLimit(items, 4, function(item) {
    return toffeeWishlistFetchProduct(item, Number(forceProductId) === item.productId);
  });

  if (renderToken !== toffeeWishlistState.renderToken) {
    return;
  }

  const fragment = document.createDocumentFragment();
  results.forEach(function(result) {
    const itemNode = toffeeWishlistBuildItem(result);
    if (itemNode) {
      fragment.appendChild(itemNode);
    }
  });

  itemsContainer.replaceChildren(fragment);
  body.setAttribute("aria-busy", "false");
  loading.hidden = true;
  itemsContainer.hidden = false;
}

async function toffeeWishlistAddToCart(button) {
  const variantId = Number(button.dataset.variantId);
  const productId = Number(button.dataset.productId);

  if (!Number.isSafeInteger(variantId) || variantId <= 0 || button.disabled) {
    return;
  }

  button.disabled = true;
  button.setAttribute("aria-busy", "true");
  const originalText = button.textContent;
  button.textContent = "Wird hinzugefügt …";

  const sections = ["variant-added"];
  document.documentElement.dispatchEvent(new CustomEvent("cart:prepare-bundled-sections", {
    bubbles: true,
    detail: {
      sections: sections
    }
  }));

  const uniqueSections = Array.from(new Set(sections));
  const formData = new FormData();
  formData.append("id", String(variantId));
  formData.append("quantity", "1");
  formData.append("sections", uniqueSections.join(","));
  formData.append("sections_url", window.location.pathname);

  try {
    const addResponse = await fetch(toffeeWishlistRootUrl() + "cart/add.js", {
      method: "POST",
      headers: {
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest"
      },
      body: formData
    });
    const addedContent = await addResponse.json();

    if (!addResponse.ok) {
      const addError = new Error(addedContent.description || "cart_add_failed");
      addError.payload = addedContent;
      throw addError;
    }

    const cartResponse = await fetch(toffeeWishlistRootUrl() + "cart.js", {
      headers: {
        Accept: "application/json"
      }
    });
    const cartContent = await cartResponse.json();
    cartContent.sections = addedContent.sections || {};
    toffeeWishlistState.cartVariantIds.add(variantId);

    const drawer = document.getElementById("toffee-wishlist-drawer");
    if (drawer && typeof drawer.hide === "function") {
      drawer.hide();
    } else if (drawer) {
      drawer.open = false;
    }

    await new Promise(function(resolve) {
      window.requestAnimationFrame(resolve);
    });

    const addedItems = Array.isArray(addedContent.items) ? addedContent.items : [addedContent];
    button.dispatchEvent(new CustomEvent("variant:add", {
      bubbles: true,
      detail: {
        items: addedItems,
        cart: cartContent
      }
    }));

    document.documentElement.dispatchEvent(new CustomEvent("cart:change", {
      bubbles: true,
      detail: {
        baseEvent: "variant:add",
        cart: cartContent
      }
    }));

    toffeeWishlistAnnounce("In den Warenkorb gelegt. Das Produkt bleibt als Lieblingsstück gespeichert.", false);
    toffeeWishlistTrack("wishlist_add_to_cart", {
      source: "drawer",
      productId: productId,
      variantId: variantId
    });
  } catch (error) {
    button.disabled = false;
    button.removeAttribute("aria-busy");
    button.textContent = originalText;

    const message = error && error.message && error.message !== "cart_add_failed"
      ? error.message
      : "Das Produkt konnte nicht in den Warenkorb gelegt werden.";

    button.dispatchEvent(new CustomEvent("cart:error", {
      bubbles: true,
      detail: {
        error: message
      }
    }));
    document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", {
      bubbles: true
    }));
    toffeeWishlistAnnounce(message, true);
    toffeeWishlistTrack("wishlist_error", {
      source: "drawer",
      productId: productId,
      variantId: variantId,
      error: "cart_add_failed"
    });
  }
}

function toffeeWishlistHandleUndo() {
  const undo = toffeeWishlistState.lastUndo;

  if (!undo) {
    return;
  }

  if (undo.type === "remove") {
    toffeeWishlistRemove(undo.item.productId, "undo");
  } else {
    toffeeWishlistRestore(undo.item, undo.index, "undo");
  }

  toffeeWishlistState.lastUndo = null;
  toffeeWishlistHideToast();
  toffeeWishlistTrack("wishlist_undo", {
    source: undo.source,
    productId: undo.item.productId,
    variantId: undo.item.variantId
  });
}

function toffeeWishlistHandleVariantEvent(event) {
  const detailVariant = event.detail && event.detail.variant ? event.detail.variant : null;
  const productInfo = event.target instanceof Element ? event.target.closest(".product-info") : null;
  const button = productInfo ? productInfo.querySelector("[data-toffee-wishlist-button]") : null;

  if (button && detailVariant && detailVariant.id) {
    button.dataset.variantId = String(detailVariant.id);

    if (toffeeWishlistFind(button.dataset.productId)) {
      toffeeWishlistUpdateVariant(button.dataset.productId, detailVariant.id);
    }
  }
}

function toffeeWishlistHandleClick(event) {
  const target = event.target instanceof Element ? event.target : null;

  if (!target) {
    return;
  }

  const toggleButton = target.closest("[data-toffee-wishlist-button]");
  if (toggleButton) {
    event.preventDefault();
    event.stopPropagation();
    toffeeWishlistToggle(toggleButton);
    return;
  }

  const opener = target.closest("[data-toffee-wishlist-open], [data-toast-view]");
  if (opener) {
    toffeeWishlistRenderDrawer();
    toffeeWishlistTrack("wishlist_drawer_open", {
      source: opener.hasAttribute("data-toast-view") ? "toast" : "header"
    });
    return;
  }

  const removeButton = target.closest("[data-remove-item]");
  if (removeButton) {
    const productId = Number(removeButton.dataset.productId);
    const itemNode = removeButton.closest("[data-toffee-wishlist-item]");
    const nextNode = itemNode ? itemNode.nextElementSibling || itemNode.previousElementSibling : null;
    const focusProductId = nextNode ? nextNode.dataset.productId : null;
    const removal = toffeeWishlistRemove(productId, "drawer");

    if (removal) {
      toffeeWishlistState.lastUndo = {
        type: "restore",
        item: removal.item,
        index: removal.index,
        source: "drawer"
      };
      toffeeWishlistShowToast("Entfernt", false);
      toffeeWishlistTrack("wishlist_remove", {
        source: "drawer",
        productId: productId,
        variantId: removal.item.variantId
      });

      toffeeWishlistRenderDrawer().then(function() {
        const focusTarget = focusProductId
          ? document.querySelector('[data-toffee-wishlist-item][data-product-id="' + focusProductId + '"] [data-remove-item]')
          : document.querySelector("#toffee-wishlist-drawer a.button");
        if (focusTarget) {
          focusTarget.focus({ preventScroll: true });
        }
      });
    }
    return;
  }

  const retryButton = target.closest("[data-retry-product]");
  if (retryButton) {
    toffeeWishlistRenderDrawer(retryButton.dataset.productId);
    return;
  }

  const addButton = target.closest("[data-add-to-cart]");
  if (addButton) {
    toffeeWishlistAddToCart(addButton);
    return;
  }

  if (target.closest("[data-toast-undo]")) {
    toffeeWishlistHandleUndo();
    return;
  }

  if (target.closest("[data-toast-close]")) {
    toffeeWishlistHideToast();
  }
}

function toffeeWishlistInitialize() {
  toffeeWishlistState.items = toffeeWishlistRead();
  toffeeWishlistSyncUi();

  document.addEventListener("click", toffeeWishlistHandleClick);
  document.addEventListener("toggle", toffeeWishlistHandleDrawerOpen);
  document.addEventListener("dialog:after-show", toffeeWishlistHandleDrawerOpen);
  document.addEventListener("variant:change", toffeeWishlistHandleVariantEvent);
  document.addEventListener("shopify:section:load", toffeeWishlistSyncUi);

  window.addEventListener("storage", function(event) {
    if (event.key === TOFFEE_WISHLIST_KEY) {
      toffeeWishlistState.items = toffeeWishlistRead();
      toffeeWishlistSyncUi();

      const drawer = document.getElementById("toffee-wishlist-drawer");
      if (drawer && (drawer.open || drawer.hasAttribute("open"))) {
        toffeeWishlistRenderDrawer();
      }
    }
  });

  window.addEventListener("pageshow", function() {
    toffeeWishlistState.items = toffeeWishlistRead();
    toffeeWishlistSyncUi();
  });

  const toast = document.querySelector("[data-toffee-wishlist-toast]");
  if (toast) {
    toast.addEventListener("mouseenter", function() {
      window.clearTimeout(toffeeWishlistState.toastTimer);
    });
    toast.addEventListener("mouseleave", toffeeWishlistArmToast);
    toast.addEventListener("focusin", function() {
      window.clearTimeout(toffeeWishlistState.toastTimer);
    });
    toast.addEventListener("focusout", function(event) {
      if (!toast.contains(event.relatedTarget)) {
        toffeeWishlistArmToast();
      }
    });
  }

  let syncScheduled = false;
  const observer = new MutationObserver(function(mutations) {
    const hasWishlistNode = mutations.some(function(mutation) {
      return Array.from(mutation.addedNodes).some(function(node) {
        return node instanceof Element && (
          node.matches("[data-toffee-wishlist-button], [data-toffee-wishlist-count]") ||
          node.querySelector("[data-toffee-wishlist-button], [data-toffee-wishlist-count]")
        );
      });
    });

    if (hasWishlistNode && !syncScheduled) {
      syncScheduled = true;
      window.requestAnimationFrame(function() {
        syncScheduled = false;
        toffeeWishlistSyncUi();
      });
    }
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", toffeeWishlistInitialize, { once: true });
} else {
  toffeeWishlistInitialize();
}
